import React, { useEffect } from 'react';

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@dashboard/components/ui/select';
import { usePreview } from '@dashboard/context/PreviewContext';
import usePage from '@dashboard/hooks/usePage';
import {
  countEnabledPrices,
  getPriceSelectLabel,
} from '@dashboard/scenes/PageEdit/scenes/PageProduct/components/PricesList/priceHelpers';

// Preview types whose rendered output resolves to a single price (so choosing one
// is meaningful). The checkout preview is excluded — it has its own price picker.
const PRICE_PREVIEW_TYPES = [
  'confirmationCheckoutMessage',
  'confirmationEmailMessage',
  'reminderEmailMessage',
];

/**
 * Preview-only price picker shown in the preview toolbar for the confirmation /
 * email previews on multi-price pages. Writes only to PreviewContext
 * (`previewPriceId`) — never to the page — so it's a preview control, not a saved
 * field. Hidden for single-price pages and previews that don't resolve to one price.
 */
export default function PreviewPriceSelect() {
  const { page } = usePage();
  const [preview, dispatch] = usePreview();

  const prices = page.product?.prices ?? [];
  const enabledPrices = prices.filter(price => price?.enabled !== false);
  const isRelevant = PRICE_PREVIEW_TYPES.includes(preview.type) && countEnabledPrices(prices) > 1;

  // Default to the merchant default price (if still enabled) or the first enabled
  // price, so the preview is never blank or stuck on a removed price.
  const defaultId =
    enabledPrices.find(price => String(price._id) === String(page.product?.defaultPriceId))?._id ??
    enabledPrices[0]?._id;
  const isCurrentValid = enabledPrices.some(
    price => String(price._id) === String(preview.previewPriceId),
  );
  const selectedId = isCurrentValid ? preview.previewPriceId : defaultId;
  const enabledKey = enabledPrices.map(price => price._id).join(',');

  // Seed / repair the selection on mount and whenever the price list changes such
  // that the stored id is no longer a current enabled price.
  useEffect(() => {
    if (isRelevant && String(preview.previewPriceId) !== String(selectedId)) {
      dispatch({
        type: 'set',
        value: { previewPriceId: selectedId ? String(selectedId) : null },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabledKey, page.product?.defaultPriceId, preview.previewPriceId, isRelevant]);

  if (!isRelevant) return null;

  return (
    <Select
      value={selectedId ? String(selectedId) : undefined}
      onValueChange={value => dispatch({ type: 'set', value: { previewPriceId: value } })}
    >
      <SelectTrigger size="sm" className="bg-white dark:bg-background" aria-label="Preview price">
        <SelectValue placeholder="Select a price" />
      </SelectTrigger>
      <SelectContent>
        {enabledPrices.map(price => (
          <SelectItem key={price._id} value={String(price._id)}>
            {getPriceSelectLabel(price)}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
